import React from 'react'
import { alpha, Typography } from '@mui/material'
import { ControlProps, components } from 'react-select'
import { makeStyles } from 'tss-react/mui'

interface StyleProps {
  isMenuOpen?: boolean
  isMulti?: boolean
  minimal?: boolean
  isFloating?: boolean
  isFocused?: boolean
  fullWidth?: boolean
  disabled?: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => {
  const gradientHeight = props.isFocused ? 15 : 14
  const inputBackground = props.disabled ? theme.shape.inputField.disabledBackgroundColor : theme.palette.background.paper

  return {
    floatingLabel: {
      position: 'absolute',
      pointerEvents: 'none',
      transition: '0.2s ease all',
      transformOrigin: 'left center',
      top: props.isFloating ? -2 : '50%',
      left: 0,
      transform: props.isFloating ? theme.shape.inputField.labelFloatingActive : theme.shape.inputField.labelFloatingInactive,
      fontSize: props.disabled ? '14px' : theme.shape.select.labelFontSize,
      color: alpha(theme.palette.text.primary, props.disabled ? 0.5 : 1),
      fontWeight: props.isFloating ? theme.shape.inputField.labelFontWeightShrinked : 'inherit',
      paddingLeft: `calc(${theme.spacing(1)} / 2)`,
      paddingRight: `calc(${theme.spacing(1)} / 2)`,
      zIndex: props.isMenuOpen ? 4 : 1,
      '&:active': {
        color: theme.palette.primary.main,
      },
      '&::after': {
        content: '" "',
        zIndex: -1,
        position: 'absolute',
        left: 0,
        width: '100%',
        height: '100%',
        background: `linear-gradient(0deg, ${inputBackground} 0%, ${inputBackground} ${gradientHeight}px, transparent ${gradientHeight}px, transparent 100%)`,
      },
      [theme.breakpoints.only('xs')]: {
        maxWidth: '100%',
      },
    },
    floatingLabelError: {
      color: theme.palette.error.main,
    },
    floatingLabelFocus: {
      color: theme.shape.inputField.labelColorActive,
    },
  }
})

/**
 * This function provides the React control item as one of the select components
 */
export const getControlItem = (
  label: string,
  required: boolean,
  dataQa?: string,
  error?: boolean,
  fullWidth?: boolean,
  minimal?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ((defaultProps: ControlProps<any, any>) => React.ReactElement) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Control = (defaultProps: ControlProps<any, any>): React.ReactElement => {
    const { isFocused, hasValue, isDisabled, menuIsOpen, isMulti } = defaultProps

    const { classes, cx } = useStyles({
      isFloating: isFocused || hasValue,
      isFocused,
      disabled: isDisabled,
      isMenuOpen: menuIsOpen,
      isMulti,
      fullWidth,
      minimal,
    })
    const labelString = [label]

    if (required) labelString.push(' *')

    return (
      <>
        <Typography
          noWrap={true}
          className={cx(classes.floatingLabel, { [classes.floatingLabelError]: !!error, [classes.floatingLabelFocus]: !!defaultProps.isFocused })}
        >
          {labelString}
        </Typography>
        <components.Control {...defaultProps} innerProps={Object.assign({}, defaultProps.innerProps, { 'data-qa': dataQa })} />
      </>
    )
  }
  return Control
}
